import { Card } from "@/components/ui/card";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from "recharts";
import { TrendingUp } from "lucide-react";
import { format, parseISO } from "date-fns";

export default function CapitalOverTimeChart({ investments }) {
  // Group investments by month and calculate cumulative capital
  const sortedInvestments = [...investments]
    .filter(inv => inv.investment_date)
    .sort((a, b) => new Date(a.investment_date) - new Date(b.investment_date));

  if (sortedInvestments.length === 0) {
    return (
      <Card className="p-6 bg-white border-0 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-zinc-900" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Kapital Over Tid</h3>
            <p className="text-sm text-slate-500">Kumulativ investert kapital</p>
          </div>
        </div>
        <div className="text-center py-12">
          <p className="text-sm text-slate-500">Ingen investeringsdata tilgjengelig</p>
        </div>
      </Card>
    );
  }

  // Create time series data
  const timeSeriesData = [];
  let cumulativeCapital = 0;
  const monthlyData = new Map();

  sortedInvestments.forEach(inv => {
    const date = new Date(inv.investment_date);
    const monthKey = format(date, "yyyy-MM");
    
    if (!monthlyData.has(monthKey)) {
      monthlyData.set(monthKey, 0);
    }
    monthlyData.set(monthKey, monthlyData.get(monthKey) + (inv.investment_amount || 0));
  });

  // Convert to array and calculate cumulative
  Array.from(monthlyData.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .forEach(([month, amount]) => {
      cumulativeCapital += amount;
      timeSeriesData.push({
        month: format(parseISO(month + "-01"), "MMM yyyy"),
        capital: cumulativeCapital / 1000000, // Convert to millions
        monthlyInvestment: amount / 1000000,
      });
    });

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-slate-200 rounded-lg shadow-lg">
          <p className="font-semibold text-slate-900 mb-2">{payload[0].payload.month}</p>
          <p className="text-sm text-slate-600">
            Kumulativ: <span className="font-bold text-zinc-900">
              {payload[0].value.toFixed(2)}M kr
            </span>
          </p>
          <p className="text-xs text-slate-500 mt-1">
            Investert denne måneden: {payload[0].payload.monthlyInvestment.toFixed(2)}M kr
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <Card className="p-6 bg-white border-0 shadow-sm">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-lime-100 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-zinc-900" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Kapital Over Tid</h3>
          <p className="text-sm text-slate-500">Kumulativ investert kapital</p>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={timeSeriesData} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
          <defs>
            <linearGradient id="colorCapital" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#c9e25a" stopOpacity={0.3}/>
              <stop offset="95%" stopColor="#c9e25a" stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis 
            dataKey="month" 
            tick={{ fill: "#64748b", fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis 
            tick={{ fill: "#64748b", fontSize: 12 }}
            label={{ value: "Kapital (M kr)", angle: -90, position: "insideLeft", style: { fill: "#64748b" } }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Area 
            type="monotone" 
            dataKey="capital" 
            stroke="#c9e25a" 
            strokeWidth={2}
            fillOpacity={1} 
            fill="url(#colorCapital)" 
          />
        </AreaChart>
      </ResponsiveContainer>
    </Card>
  );
}